import { AlertCircle } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Navigate, useSearchParams } from 'react-router';
import { errorMessage, isApiError, safeNextPath } from '@/api/client';
import { useLogin, useMe } from '@/api/hooks/auth';
import { Button, Field, Input, LoadingBlock } from '@/components';
import { AuthLayout } from './AuthLayout';
import styles from './forms.module.css';

function loginError(error: unknown): string {
  if (isApiError(error)) {
    if (error.status === 401) return 'Wrong email or password.';
    if (error.status === 429) {
      const wait = Number(error.extra?.retry_after_s);
      return `Too many failed attempts. Try again in ${Number.isFinite(wait) ? Math.ceil(wait / 60) : 15} minutes.`;
    }
  }
  return errorMessage(error);
}

export default function Login() {
  const [params] = useSearchParams();
  const next = safeNextPath(params.get('next'));
  const me = useMe();
  const login = useLogin();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  if (me.isPending) return <LoadingBlock />;
  if (me.data) return <Navigate to={next} replace />;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    // on success `me` is set and the <Navigate> above takes the user to `next`
    login.mutate({ email: email.trim(), password });
  };

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Annotation platform for hyperspectral cubes."
      footer="Accounts are created by an administrator — ask them for an invite link."
    >
      <form className={styles.form} onSubmit={onSubmit} noValidate>
        {login.isError && (
          <div className={styles.alert} role="alert">
            <AlertCircle aria-hidden />
            <span>{loginError(login.error)}</span>
          </div>
        )}
        <Field label="Email">
          <Input
            type="email"
            name="email"
            autoComplete="username"
            autoFocus
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label="Password">
          <Input
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <Button
          type="submit"
          variant="primary"
          loading={login.isPending}
          disabled={!email.trim() || !password}
        >
          Sign in
        </Button>
      </form>
    </AuthLayout>
  );
}
